import { Grid } from "@mui/material";
import { useEffect, useCallback, useMemo } from "react";
import { ActionButton } from "../../../../components/Button";
import Dialog from "../../../../components/Dialog";
import { IAddEditDialogProps } from "../../../../components/Dialog";
import TextField from "../../../../components/TextField";
import Switch from "../../../../components/Switch";
import { useLanguageContext } from "../../../../contexts/LanguageContext";
import IGovernanceItem from "../../../../interfaces/IGovernanceItem";
import ValidationHelper from '../../../../helpers/ValidationHelper';
import DefaultConstants from "../../../../data/Constants";
import { RegistrationStatusEnum } from "../../../../data/Enums";
import FormHelper from "../../../../helpers/FormHelper";
import GovernanceItemService from '../../../../services/GovernanceItemService';
import SelectField from "../../../../components/SelectField";
import { useAuthContext } from "../../../../contexts/AuthContext";
import RichTextEditor from "../../../../components/RichTextEditor";
import useState from "../../../../hooks/useState";


interface IStateValues {
    name: string;
    orderBy: number;
    required: boolean;
    active: boolean;
    description: string;
    roomTypes: number[];
}


const AddEditDialog: React.FunctionComponent<IAddEditDialogProps<IGovernanceItem>> = ({
    open,
    handleClose,
    data,
    handleListRefresh = () => {}
}) => {
    const { translate } = useLanguageContext();
    const { enterprise } = useAuthContext();

    const state = useState<IStateValues>({
        name: '',
        orderBy: 0,
        required: false,
        active: true,
        description: '',
        roomTypes: []
    });


    useEffect(() => {
        if (open) {
            if (data) {
                state.update('name', data.name);
                state.update('orderBy', data.orderBy);
                state.update('required', data.required);
                state.update('active', Number(data.registrationStatusId) === RegistrationStatusEnum.Active);
                state.update('description', data.description);
                state.update('roomTypes', data.roomTypes);
            } else {
                state.reset();
            }
        }

        // eslint-disable-next-line
    }, [open]);


    const handleSubmit = useCallback((event: React.FormEvent<HTMLFormElement>) => {
        event.preventDefault();

        let formData = FormHelper.Create(event);
        if (data?.id && data.id !== 0) {
            formData = FormHelper.Add(formData, 'id', data.id);
        }

        GovernanceItemService.AddEdit(formData).then(() => {
            handleClose();
            handleListRefresh();
        });
        
        // eslint-disable-next-line
    }, [data]);


    const dialogActions = useMemo(() => {
        return [
            <ActionButton action='cancel' onClick={handleClose} />,
            <ActionButton action='save' type='submit' />
        ];
        // eslint-disable-next-line
    }, [translate]);


    return (
        <Dialog
            open={open}
            handleClose={handleClose}
            title={translate(data && data.id !== 0 ? 'edit' : 'add')}
            maxWidth='md'
            fullWidth={true}
            fullHeight={true}
            dialogActions={dialogActions}
            onSubmit={handleSubmit}
            className='dialogactions-spacebetween'
        >
            <Grid container columnSpacing={DefaultConstants.gridColumnSpacing}>
                <Grid item md={8} xs={12}>
                    <TextField 
                        label={translate('name')} 
                        name='name' 
                        value={state.values.name} 
                        onChange={e => state.update(e.target.name, ValidationHelper.MaxLength(e.target.value, 100))}
                    />
                </Grid>

                <Grid item md={4} xs={12}>
                    <TextField 
                        type="number"
                        label={translate('ordering')} 
                        name='orderBy' 
                        value={state.values.orderBy} 
                        onChange={e => state.update(e.target.name, ValidationHelper.MaxLength(e.target.value, 10))}
                        required={true}
                    />
                </Grid>

                <Grid item md={6} xs={12}>
                    <TextField 
                        label={translate('token')}
                        value={data?.token ?? ''} 
                        disabled={true}
                        required={false}
                    />
                </Grid>

                <Grid item md={6} xs={12}>
                    <SelectField
                        label={translate('roomTypes')}
                        name='roomTypes'
                        value={state.values.roomTypes.length > 0 ? state.values.roomTypes : [0]}
                        multiple={true}
                        onChange={e => {
                            const values = e.target.value as number[];
                            state.update(e.target.name, state.values.roomTypes.length > 0 && values.includes(0) ? [] : values.filter(v => v !== 0));
                        }}
                        data={[{ value: 0, text: translate('all') }].concat((enterprise?.partnerPms.roomTypes ?? []).map(r => {
                            return {
                                value: +r.id,
                                text: r.description
                            };
                        }))}
                    />
                </Grid>

                <Grid item md={9} xs={12} className='textalign-right'>
                    <Switch
                        label={translate('required')}
                        name='required'
                        value={state.values.required}
                        checked={state.values.required}
                        onChange={e => state.update(e.target.name, e.target.checked)}
                    />
                </Grid>

                <Grid item md={3} xs={12} className='textalign-right'>
                    <Switch
                        label={translate('active')}
                        name='registrationStatusId'
                        value={state.values.active ? RegistrationStatusEnum.Active : RegistrationStatusEnum.Inactive}
                        checked={state.values.active}
                        onChange={e => state.update('active', e.target.checked)}
                    />
                </Grid>

                <Grid item xs={12} marginTop={2}>
                    <RichTextEditor 
                        label={translate('description')}
                        name="description"
                        value={state.values.description}
                        handleChange={value => state.update('description', value)}
                    />
                </Grid>
            </Grid>
        </Dialog>
    );
}

export default AddEditDialog;